/*
 * The 21 instruments of the descent: one living diagram per practice of the
 * Lean Pyramid 6.0. Everything draws in currentColor so a scene wears its
 * layer accent. Motion is CSS keyframes (see input.css, "instruments");
 * under prefers-reduced-motion the family freezes in authored poses.
 *
 * Instruments are decorative: the scene announces the station through its
 * own text and a debounced live region, so every svg here is aria-hidden.
 * Words inside the drawings come from the `t` labels object (canonContent
 * gfx block) so they speak the page's language; universal jargon (PDCA,
 * CD3, WHY, build/test/deploy, version tags) stays as-is by design.
 */

const VB = { viewBox: '0 0 320 180', width: '100%', style: { maxHeight: '26vh' }, 'aria-hidden': true }
const stroke = { fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' }
const faint = { ...stroke, opacity: 0.3 }

/* 01 · Start with purpose: the golden circle draws itself around WHY */
const GoldenCircle = ({ t }) => (
  <svg {...VB}>
    {[64, 44, 24].map((r, i) => (
      <circle key={r} cx="160" cy="90" r={r} {...stroke} opacity={0.35 + i * 0.3}
        className="inst-stroke" style={{ '--len': 2 * Math.PI * r, animationDelay: `${i * 0.35}s` }} />
    ))}
    <text x="160" y="95" textAnchor="middle" fontSize="13" fill="currentColor" className="inst-blink" style={{ fontWeight: 600, letterSpacing: 2 }}>WHY</text>
    <text x="238" y="52" fontSize="10" fill="currentColor" opacity="0.6">{t.how}</text>
    <text x="258" y="94" fontSize="10" fill="currentColor" opacity="0.6">{t.what}</text>
  </svg>
)

/* 02 · Principles and values: agreements ticking, safety ring holding */
const Agreements = ({ t }) => (
  <svg {...VB}>
    <circle cx="238" cy="90" r="46" {...faint} className="inst-breathe-o" />
    <circle cx="238" cy="90" r="34" {...stroke} opacity="0.6" />
    <text x="238" y="94" textAnchor="middle" fontSize="9" fill="currentColor" opacity="0.8" letterSpacing="1.5">{t.safety}</text>
    {[0, 1, 2, 3].map((i) => (
      <g key={i}>
        <line x1="40" y1={46 + i * 28} x2="150" y2={46 + i * 28} {...faint} strokeWidth="6" strokeLinecap="round" />
        <path d={`M28 ${44 + i * 28} l4 5 l7 -9`} {...stroke} className="inst-tick" style={{ animationDelay: `${0.4 + i * 0.5}s` }} />
      </g>
    ))}
  </svg>
)

/* 03 · Develop people: the situational ladder, a dot climbing it */
const Ladder = ({ t }) => (
  <svg {...VB}>
    {[0, 1, 2, 3].map((i) => (
      <line key={i} x1={60 + i * 56} y1={146 - i * 32} x2={116 + i * 56} y2={146 - i * 32} {...stroke} opacity={0.35 + i * 0.15} />
    ))}
    <circle r="7" fill="currentColor" className="inst-ladder" />
    {t.ladder.map((w, i) => (
      <text key={w} x={88 + i * 56} y={166 - i * 32} textAnchor="middle" fontSize="9" fill="currentColor" opacity="0.7">{w}</text>
    ))}
  </svg>
)

/* 04 · Vision: the horizon, the flag, mission tests ticking green */
const Horizon = ({ t }) => (
  <svg {...VB}>
    <line x1="20" y1="120" x2="300" y2="120" {...faint} />
    <path d="M244 120 V58" {...stroke} className="inst-stroke" style={{ '--len': 70 }} />
    <path d="M244 58 h36 l-10 11 l10 11 h-36" fill="currentColor" opacity="0.85" className="inst-tick" style={{ animationDelay: '0.7s' }} />
    {[0, 1, 2].map((i) => (
      <g key={i} className="inst-tick" style={{ animationDelay: `${1 + i * 0.4}s` }}>
        <circle cx={46 + i * 34} cy="146" r="9" {...stroke} opacity="0.5" />
        <path d={`M${41 + i * 34} 146 l4 4 l6 -8`} {...stroke} />
      </g>
    ))}
    <text x="150" y="150" fontSize="9" fill="currentColor" opacity="0.7">{t.missionTests}</text>
  </svg>
)

/* 05 · Breakthrough objectives: many candidates, few stay lit */
const FiveBars = () => (
  <svg {...VB}>
    {[92, 58, 120, 74, 104].map((h, i) => (
      <rect key={i} x={44 + i * 50} y={150 - h} width="26" height={h} rx="4"
        fill="currentColor" className={i === 1 || i === 3 ? 'inst-dimcycle' : ''} opacity="0.9" />
    ))}
  </svg>
)

/* 06 · Deploy strategy: the catchball travels down the levels and back */
const Catchball = ({ t }) => (
  <svg {...VB}>
    {[0, 1, 2].map((i) => (
      <line key={i} x1="70" y1={44 + i * 44} x2="250" y2={44 + i * 44} {...faint} strokeWidth="5" strokeLinecap="round" />
    ))}
    <circle r="8" fill="currentColor" className="inst-catchball" />
    <text x="262" y="48" fontSize="9" fill="currentColor" opacity="0.7">{t.board}</text>
    <text x="262" y="136" fontSize="9" fill="currentColor" opacity="0.7">{t.teams}</text>
  </svg>
)

/* 07 · Break down goals: one block cascades into the hours */
const Cascade = ({ t }) => (
  <svg {...VB}>
    <rect x="120" y="20" width="80" height="22" rx="4" fill="currentColor" opacity="0.9" />
    {[0, 1].map((i) => (
      <rect key={i} x={78 + i * 90} y="62" width="74" height="18" rx="4" fill="currentColor" opacity="0.65" className="inst-tick" style={{ animationDelay: `${0.3 + i * 0.2}s` }} />
    ))}
    {[0, 1, 2, 3].map((i) => (
      <rect key={i} x={48 + i * 60} y="102" width="48" height="15" rx="3" fill="currentColor" opacity="0.45" className="inst-tick" style={{ animationDelay: `${0.8 + i * 0.15}s` }} />
    ))}
    {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
      <rect key={i} x={34 + i * 32} y="138" width="24" height="11" rx="2.5" fill="currentColor" opacity="0.3" className="inst-tick" style={{ animationDelay: `${1.4 + i * 0.08}s` }} />
    ))}
    <text x="26" y="34" fontSize="9" fill="currentColor" opacity="0.7">{t.years}</text>
    <text x="26" y="148" fontSize="9" fill="currentColor" opacity="0.7">{t.hours}</text>
  </svg>
)

/* 08 · Value stream: flow between stations; the wait shrinks */
const Stream = ({ t }) => (
  <svg {...VB}>
    {[50, 130, 240].map((x) => (
      <rect key={x} x={x - 22} y="74" width="44" height="32" rx="6" {...stroke} opacity="0.7" />
    ))}
    <line x1="72" y1="90" x2="108" y2="90" {...stroke} className="inst-flowdash" />
    <line x1="152" y1="90" x2="218" y2="90" stroke="#e2574c" strokeWidth="6" strokeLinecap="round" className="inst-wait" />
    <text x="185" y="70" textAnchor="middle" fontSize="9" fill="#e2574c" opacity="0.85">{t.wait}</text>
  </svg>
)

/* 09 · Optimize flow: cards pulled across a board, WIP held */
const BoardPull = ({ t }) => (
  <svg {...VB}>
    {[0, 1, 2].map((i) => (
      <rect key={i} x={40 + i * 90} y="30" width="70" height="120" rx="8" {...faint} />
    ))}
    <text x="75" y="22" textAnchor="middle" fontSize="9" fill="currentColor" opacity="0.7">{t.queue}</text>
    <text x="165" y="22" textAnchor="middle" fontSize="9" fill="currentColor" opacity="0.75">{t.doing}</text>
    <text x="255" y="22" textAnchor="middle" fontSize="9" fill="currentColor" opacity="0.7">{t.done}</text>
    {[0, 1, 2].map((i) => (
      <rect key={i} x="50" width="50" height="16" rx="3" fill="currentColor" opacity="0.8"
        className="inst-cardstep" style={{ animationDelay: `${i * 2.2}s`, '--row': `${44 + i * 26}px`, transform: `translate(0, ${44 + i * 26}px)` }} />
    ))}
  </svg>
)

/* 10 · Economics: the queue reorders itself by cost of delay */
const CD3Sort = () => (
  <svg {...VB}>
    {[
      { w: 150, y: 0, cls: 'inst-swap-down', label: 'CD3 3' },
      { w: 230, y: 1, cls: 'inst-swap-up', label: 'CD3 8' },
      { w: 90, y: 2, cls: '', label: 'CD3 1' },
    ].map((b, i) => (
      <g key={i} className={b.cls}>
        <rect x="46" y={44 + b.y * 34} width={b.w} height="20" rx="4" fill="currentColor" opacity={0.85 - i * 0.2} />
        <text x={54 + b.w} y={58 + b.y * 34} fontSize="9" fill="currentColor" opacity="0.7">{b.label}</text>
      </g>
    ))}
  </svg>
)

/* 11 · Capacity: queues breathe; the forecast is a fan of dates */
const MonteCarlo = ({ t }) => (
  <svg {...VB}>
    {[0, 1, 2].map((i) => (
      <rect key={i} x="30" y={38 + i * 22} width={150 - i * 34} height="10" rx="4" fill="currentColor" opacity={0.8 - i * 0.2}
        className="inst-breathe" style={{ animationDelay: `${i * 0.5}s` }} />
    ))}
    {[0, 1, 2].map((i) => (
      <path key={i} d={`M30 132 C 110 ${120 - i * 14}, 190 ${118 - i * 18}, 288 ${112 - i * 22}`} {...stroke}
        strokeDasharray="4 5" opacity={0.7 - i * 0.2} className="inst-flowdash" style={{ animationDelay: `${i * 0.4}s` }} />
    ))}
    <text x="222" y="160" fontSize="9" fill="currentColor" opacity="0.75">{t.forecast}</text>
  </svg>
)

/* 12 · Make it visual: events land in the record, the board lights up */
const LiveBoard = ({ t }) => (
  <svg {...VB}>
    {[0, 1, 2, 3].map((i) => (
      <line key={i} x1="34" y1={44 + i * 26} x2="120" y2={44 + i * 26} {...stroke} strokeWidth="5" opacity="0.5"
        className="inst-tick" style={{ animationDelay: `${i * 0.6}s` }} />
    ))}
    <text x="34" y="30" fontSize="9" fill="currentColor" opacity="0.7">{t.record}</text>
    <path d="M138 90 h34" {...stroke} className="inst-flowdash" />
    <rect x="186" y="34" width="104" height="112" rx="8" {...faint} />
    {[0, 1, 2, 3].map((i) => (
      <rect key={i} x={198 + (i % 2) * 44} y={46 + Math.floor(i / 2) * 34} width="36" height="22" rx="4" fill="currentColor" opacity="0.65"
        className="inst-tick" style={{ animationDelay: `${0.3 + i * 0.6}s` }} />
    ))}
  </svg>
)

/* 13 · Review relentlessly: the PDCA wheel turns while the wedge holds */
const PDCASlope = ({ t }) => (
  <svg {...VB}>
    <line x1="24" y1="150" x2="296" y2="52" {...faint} />
    <g className="inst-spin" style={{ transformOrigin: '120px 96px' }}>
      <circle cx="120" cy="96" r="26" {...stroke} strokeDasharray="34 8" />
    </g>
    <text x="120" y="100" textAnchor="middle" fontSize="10" fill="currentColor" opacity="0.85">PDCA</text>
    <path d="M96 130 L146 130 L120 122 Z" fill="currentColor" opacity="0.35" />
    <text x="200" y="150" fontSize="9" fill="currentColor" opacity="0.7">{t.standardHolds}</text>
  </svg>
)

/* 14 · The vault: standards versioned like code */
const Vault = () => (
  <svg {...VB}>
    <rect x="60" y="30" width="132" height="120" rx="8" {...stroke} opacity="0.6" />
    {[0, 1, 2, 3].map((i) => (
      <line key={i} x1="76" y1={54 + i * 24} x2={168 - (i % 2) * 30} y2={54 + i * 24} {...stroke} strokeWidth="4" opacity="0.4" />
    ))}
    {['v4', 'v5', 'v6'].map((v, i) => (
      <g key={v} className="inst-fadecycle" style={{ animationDelay: `${i * 1.4}s` }}>
        <circle cx="232" cy={54 + i * 34} r="5" fill="currentColor" />
        <text x="246" y={58 + i * 34} fontSize="10" fill="currentColor" opacity="0.7">{v}</text>
      </g>
    ))}
    <line x1="232" y1="54" x2="232" y2="122" {...faint} />
  </svg>
)

/* 15 · Architecture just in time: modules settle into clean seams */
const Seams = () => (
  <svg {...VB}>
    {[0, 1, 2, 3].map((i) => (
      <rect key={i} width="58" height="42" rx="6" {...stroke} opacity="0.7" className={`inst-settle-${i}`} />
    ))}
    <line x1="160" y1="40" x2="160" y2="140" {...faint} strokeDasharray="4 6" />
    <line x1="96" y1="90" x2="224" y2="90" {...faint} strokeDasharray="4 6" />
  </svg>
)

/* 16 · Code to value: every line carries its why; unused code leaves */
const WhyTag = ({ t }) => (
  <svg {...VB}>
    {[0, 1, 2].map((i) => (
      <line key={i} x1="48" y1={52 + i * 30} x2={210 - i * 24} y2={52 + i * 30} {...stroke} strokeWidth="6" opacity="0.55" />
    ))}
    <g className="inst-tick" style={{ animationDelay: '0.5s' }}>
      <rect x="226" y="42" width="52" height="20" rx="10" {...stroke} />
      <text x="252" y="56" textAnchor="middle" fontSize="9" fill="currentColor">{t.why}</text>
    </g>
    <g className="inst-fadeout">
      <line x1="48" y1="142" x2="186" y2="142" {...stroke} strokeWidth="6" />
      <line x1="196" y1="136" x2="208" y2="148" {...stroke} strokeWidth="2" />
      <line x1="208" y1="136" x2="196" y2="148" {...stroke} strokeWidth="2" />
    </g>
  </svg>
)

/* 17 · Clean code: the sweep passes and the smells vanish */
const Sweep = () => (
  <svg {...VB}>
    {[0, 1, 2, 3, 4].map((i) => (
      <line key={i} x1="40" y1={40 + i * 26} x2={240 - (i % 3) * 40} y2={40 + i * 26} {...stroke} strokeWidth="5" opacity="0.5" />
    ))}
    {[[80, 66], [180, 92], [130, 118], [210, 40]].map(([x, y], i) => (
      <circle key={i} cx={x} cy={y} r="4" fill="#e2574c" className="inst-vanish" style={{ animationDelay: `${0.8 + i * 0.7}s` }} />
    ))}
    <line y1="26" y2="150" {...stroke} opacity="0.9" className="inst-sweepline" />
  </svg>
)

/* 18 · Test first: red, green, refactor, forever */
const RGR = ({ t }) => (
  <svg {...VB}>
    {[
      ['#e2574c', t.rgr[0], 0],
      ['#c9d33a', t.rgr[1], 1],
      ['currentColor', t.rgr[2], 2],
    ].map(([col, label, i]) => (
      <g key={label}>
        <circle cx={90 + i * 70} cy="82" r="20" fill={col} opacity="0.85" className="inst-rgr" style={{ animationDelay: `${i * 1.2}s` }} />
        <text x={90 + i * 70} y="126" textAnchor="middle" fontSize="10" fill="currentColor" opacity="0.7">{label}</text>
      </g>
    ))}
    <path d="M110 82 h28 M180 82 h28 M244 66 C 250 30, 80 30, 82 60" {...faint} strokeDasharray="4 5" className="inst-flowdash" />
  </svg>
)

/* 19 · Path to production: commits ride the pipeline through its gates */
const Pipeline = () => (
  <svg {...VB}>
    <line x1="30" y1="90" x2="290" y2="90" {...faint} strokeWidth="3" />
    {[100, 170, 240].map((x) => (
      <rect key={x} x={x - 12} y="66" width="24" height="48" rx="6" {...stroke} opacity="0.6" />
    ))}
    {['build', 'test', 'deploy'].map((w, i) => (
      <text key={w} x={100 + i * 70} y="136" textAnchor="middle" fontSize="9" fill="currentColor" opacity="0.7">{w}</text>
    ))}
    {[0, 1, 2].map((i) => (
      <circle key={i} r="6" fill="currentColor" className="inst-commit"
        style={{ animationDelay: `${i * 2}s`, transform: `translate(${44 + i * 88}px, 90px)` }} />
    ))}
  </svg>
)

/* 20 · Direct the agents: the pod works; the andon stops the line */
const Andon = ({ t }) => (
  <svg {...VB}>
    <line x1="36" y1="90" x2="284" y2="90" {...faint} strokeWidth="3" />
    {[70, 130, 190].map((x, i) => (
      <circle key={x} cx={x} cy="90" r="9" fill="currentColor" opacity={0.8 - i * 0.15} />
    ))}
    <circle cx="250" cy="90" r="9" fill="#f88116" className="inst-andon" />
    <path d="M250 74 V44 h14" {...stroke} opacity="0.7" />
    <text x="160" y="140" textAnchor="middle" fontSize="9" fill="currentColor" opacity="0.7">{t.andonLine}</text>
  </svg>
)

/* 21 · Monitor continuously: the heartbeat of systems and agents */
const Heartbeat = ({ t }) => (
  <svg {...VB}>
    <path d="M24 96 H96 l14 -34 l20 62 l16 -42 l10 14 H296" {...stroke} className="inst-ecg" />
    {[0, 1, 2].map((i) => (
      <circle key={i} cx={110 + i * 44} cy="150" r="5" fill="currentColor" className="inst-blink" style={{ animationDelay: `${i * 0.7}s` }} />
    ))}
    <text x="196" y="154" fontSize="9" fill="currentColor" opacity="0.7">{t.systems}</text>
  </svg>
)

/* station index (0-20, apex to base) -> instrument */
const INSTRUMENTS = [
  GoldenCircle, Agreements, Ladder, Horizon, FiveBars, Catchball,
  Cascade, Stream, BoardPull, CD3Sort, MonteCarlo, LiveBoard, PDCASlope, Vault,
  Seams, WhyTag, Sweep, RGR, Pipeline, Andon, Heartbeat,
]

export default INSTRUMENTS
